import { Pagination } from "../types/pagination";
import { PropertyCalendarBlockDetail } from "../types/property-calendar-blocks";
import {
  GenerateQuote,
  MonthlyRevenueItem,
  RentalCalendarAgent,
  RentalCalendarDayReservation,
  RentalCalendarProperty,
  RentalCalendarPropertyDetails,
  RentalCalendarSort,
  RentalCalendarTentative,
  RentalCalendarTentativeRate,
  TentativeRatesArg,
} from "../types/rental-calendar";
import { Coupon, PriceBreakdown } from "../types/rental-payment";
import { IRentalCalendarService } from "../types/services/rental-calendar-service";
import { DELETE, FetchResult, GET, PATCH, POST } from "../utils/fetch";

class RentalCalendarService implements IRentalCalendarService {
  async getCalendar(
    start_date?: string,
    end_date?: string,
    location_id?: string,
    propertyHighlight_id?: string[],
    propertyType?: string[],
    bedroom_id?: string,
    adult_id?: string,
    property_slug?: string,
    config_id?: string,
    checkinDate?: string,
    checkoutDate?: string,
    property_detail?: string,
    search?: string,
    page?: string,
    page_size?: string,
    include_active?: boolean,
    sort_by: RentalCalendarSort = "recommended",
  ): Promise<FetchResult<RentalCalendarProperty[]>> {
    const qp = new URLSearchParams();

    if (start_date !== undefined) {
      qp.set("start_date", start_date);
    }

    if (end_date !== undefined) {
      qp.set("end_date", end_date);
    }

    if (checkinDate !== undefined) {
      qp.set("check_in", checkinDate);
    }

    if (checkoutDate !== undefined) {
      qp.set("check_out", checkoutDate);
    }

    if (location_id !== undefined) {
      qp.set("location_id", location_id.toString());
    }

    if (propertyHighlight_id?.length) {
      propertyHighlight_id.forEach((each) => qp.append("highlights[]", each));
    }

    if (propertyType?.length) {
      propertyType.forEach((each) => qp.append("property_types[]", each));
    }

    if (bedroom_id !== undefined) {
      qp.set("bedrooms", bedroom_id.toString());
    }

    if (adult_id !== undefined) {
      qp.set("occupancy", adult_id.toString());
    }

    if (property_slug !== undefined) {
      qp.set("property_slug", property_slug);
    }

    if (config_id !== undefined) {
      qp.set("property_config_id", config_id);
    }

    if (property_detail !== undefined) {
      qp.set("sales_channel", property_detail);
    }

    if (search) {
      qp.set("query", search);
    }

    if (page) {
      qp.set("page", page);
    }

    if (page_size) {
      qp.set("per_page", page_size);
    }

    if (include_active) {
      qp.set("include_active", "true");
    }

    qp.set("sort_by", sort_by);

    const url = `/api/v2/rental/calendar?${qp}`,
      { error, response } = await GET<RentalCalendarResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: null,
      response: response.properties,
      paginate: response.paginate,
    };
  }

  async getTentativeRates(
    args: TentativeRatesArg[],
    global?: Coupon,
    opportunitySlug?: string,
    existingReservationSlug?: string,
  ): Promise<FetchResult<RentalCalendarTentative>> {
    if (!args.length) {
      return {
        error: null,
        response: null,
      };
    }

    const payload: any = {
      reservations: args.map((each) => {
        const {
            agent_id,
            property_slug,
            checkin_date,
            checkout_date,
            adults,
            children,
            coupon,
            voucher_code,
            breakdown,
            config_id,
            is_opted,
            display_protect_widget,
            quote_id,
          } = each,
          formatted: any = {
            agent_id: agent_id,
            property_slug: property_slug,
            check_in: checkin_date,
            check_out: checkout_date,
            adults: adults || "1",
            property_config_id: config_id,
            is_opted: is_opted,
            display_protect_widget: display_protect_widget,
            quote_id: quote_id,
          };

        if (children) {
          formatted.children = children;
        }

        if (coupon) {
          if (coupon.code === "MANUAL") {
            formatted.discount_type = coupon.type;
            formatted.discount_value = coupon.value;
          } else {
            formatted.discount_type = "coupon";
            formatted.discount_value = coupon.code;
          }
        }

        if (voucher_code) {
          formatted.voucher_code = voucher_code;
        }

        if (breakdown) {
          const {
            deposit,
            preferred_rate,
            agent_cutting_commission,
            commission_gst_applicable,
            commission_tds_applicable,
            commission_percentage,
            gst_commission_percentage,
            tds_commission_percentage,
            tds,
            acquisition_cost,
            ivpl_budget,
            ivpl_cc_department,
            ihpl_cc_department,
            premium,
            loyalty_points_applied,
            cost_centres,
          } = breakdown;

          formatted.preferred_rate = preferred_rate;

          formatted.deposit = deposit;

          formatted.agent_cutting_commission = agent_cutting_commission;

          formatted.commission_gst_applicable = commission_gst_applicable;

          formatted.commission_tds_applicable = commission_tds_applicable;

          formatted.commission_percentage = commission_percentage;

          formatted.gst_commission_percentage = gst_commission_percentage;

          formatted.tds_commission_percentage = tds_commission_percentage;

          formatted.tds = tds;

          const isVoucherBooking = !!voucher_code;
          const hasCC =
            cost_centres &&
            ((cost_centres.ihpl_cost_centres?.length ?? 0) > 0 ||
              (cost_centres.ivpl_cost_centres?.length ?? 0) > 0);

          if (hasCC) {
            if (isVoucherBooking) {
              // Voucher case (calendar or trips): send caq=0. Backend doesn't
              // need our sums — calendar's backend computes from coupon, trips'
              // backend can't recompute force-booked totals. The modal computes
              // the displayed total locally from initialVccbMap when needed.
              formatted.caq = 0;
              formatted.ivpl_budget = 0;
            } else {
              // Manual cost centres: send sums so backend subtracts and returns
              // the correct payable total.
              const ihplSum = cost_centres!.ihpl_cost_centres?.reduce(
                (sum: number, c: any) => sum + (c.amount ?? 0), 0,
              ) ?? 0;
              const ivplSum = cost_centres!.ivpl_cost_centres?.reduce(
                (sum: number, c: any) => sum + (c.amount ?? 0), 0,
              ) ?? 0;
              formatted.caq = ihplSum;
              formatted.ivpl_budget = ivplSum;
              if (existingReservationSlug) {
                formatted.cost_centres = cost_centres;
              }
            }
          } else if (isVoucherBooking) {
            // Voucher applied but cost_centres not yet returned by backend
            formatted.caq = 0;
            formatted.ivpl_budget = 0;
          } else {
            formatted.caq = acquisition_cost;
            formatted.ivpl_budget = ivpl_budget;
            formatted.ivpl_cc_department = ivpl_cc_department;
            formatted.ihpl_cc_department = ihpl_cc_department;
          }

          formatted.premium = premium;

          formatted.points_applied = loyalty_points_applied ?? false;

          if (existingReservationSlug) {
            formatted.reservation_slug = existingReservationSlug;
          }
        }
        return formatted;
      }),
    };

    if (opportunitySlug) {
      payload.opportunity_slug = opportunitySlug || null;
    }

    if (global) {
      const { type, value } = global;
      payload.discount_type = type;
      payload.discount_value = value;
    }

    const url = existingReservationSlug
        ? "/api/v2/rental/calendar/updated_rates"
        : "/api/v2/rental/calendar/rates",
      { error, response } = await POST<TentativeRatesResponse>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const tentativeRates = response.rates.map((each: any, index: number) => {
        const arg = args[index],
          {
            amount,
            breakdown: rawBreakdown,
            property_slug,
            property_name,
            number_of_nights,
            number_of_marketing_nights,
            property_config_id = arg.config_id,
            display_protect_widget,
            is_opted,
            widget_details,
            refundable_booking_opted,
            points_applied,
            ineligible_referee_points,
            voucher_code: rateVoucherCode,
          } = each,
          {
            average_base_rate,
            average_adults_rate,
            average_children_rate,
            gross_base_rate,
            total_taxes,
            convenience_fee,
            preferred_rate,
            deposit,
            commission,
            agent_cutting_commission,
            commission_gst_applicable,
            commission_tds_applicable,
            commission_percentage,
            gst_commission_percentage,
            tds_commission_percentage,
            discount,
            discount_type,
            discount_value,
            coupon_code,
            coupon_method,
            voucher_code: breakdownVoucherCode,
            tds,
            caq,
            ivpl_budget,
            ivpl_cc_department,
            ihpl_cc_department,
            premium,
            currency_symbol,
            extra_adults_fare,
            loyalty_points_credited,
            available_points,
            points_redeemed,
            cost_centres,
            redeem_points_allowed,
            redeemable_points_amount,
          } = rawBreakdown || ({} as any),
          breakdown: PriceBreakdown = {
            average_base_rate: average_base_rate || 0,
            average_adults_rate: average_adults_rate || 0,
            average_children_rate: average_children_rate || 0,
            gross_base_rate: gross_base_rate || 0,
            taxes: total_taxes || 0,
            convenience_fee: convenience_fee || 0,
            preferred_rate: preferred_rate || 0,
            deposit: deposit || 0,
            commission: commission || 0,
            agent_cutting_commission: agent_cutting_commission,
            commission_gst_applicable: commission_gst_applicable,
            commission_tds_applicable: commission_tds_applicable,
            commission_percentage: commission_percentage || 0,
            gst_commission_percentage: gst_commission_percentage || 0,
            tds_commission_percentage: tds_commission_percentage || 0,
            discount: discount || 0,
            discount_type: discount_type || undefined,
            discount_value: discount_value || undefined,
            coupon_code: coupon_code || undefined,
            coupon_method: coupon_method || undefined,
            voucher_code: rateVoucherCode || breakdownVoucherCode || undefined,
            tds: tds || undefined,
            acquisition_cost: caq || undefined,
            ivpl_budget: ivpl_budget || undefined,
            ivpl_cc_department: ivpl_cc_department || undefined,
            ihpl_cc_department: ihpl_cc_department || undefined,
            premium: premium || undefined,
            currency_symbol: currency_symbol || "",
            extra_adults_fare: extra_adults_fare || 0,
            loyalty_points_credited: loyalty_points_credited || 0,
            points_applied: points_applied || false,
            available_points: available_points || 0,
            points_redeemed: points_redeemed || 0,
            cost_centres: cost_centres || undefined,
            redeem_points_allowed: redeem_points_allowed ?? true,
            redeemable_points_amount: redeemable_points_amount ?? undefined,
          },
          formatted: RentalCalendarTentativeRate = {
            ...arg,
            property_name: property_name || "",
            property_slug: property_slug || args[index].property_slug,
            amount: amount ?? 0,
            breakdown: breakdown,
            number_of_nights,
            number_of_marketing_nights,
            config_id: property_config_id,
            is_opted: is_opted || true,
            display_protect_widget:
              display_protect_widget || args[index].display_protect_widget,
            quote_id: widget_details.quote_id || arg.quote_id,
            show_refundable_switch: refundable_booking_opted,
            ineligible_referee_points: ineligible_referee_points || 0,
          };

        let coupon: Coupon | undefined = undefined;

        if (discount_type) {
          if (discount_type === "coupon") {
            coupon = arg.coupon;
          } else {
            coupon = {
              id: -1,
              code: "MANUAL",
              name: "MANUAL",
              type: discount_type,
              value: discount_value,
              active: false,
              auto_applicable_on_website: false,
              visible_on_website: false,
              tax_on_gross_amount: false,
            };
          }
        }

        formatted.coupon = coupon;

        return formatted;
      }),
      { total = {} } = response,
      totalFormatted = {
        ...total,
        breakdown: {
          ...(total.breakdown ?? {}),
          taxes: total.breakdown?.total_taxes ?? 0,
          currency_symbol: total.breakdown?.currency_symbol || "",
          loyalty_points_credited:
            total.breakdown?.loyalty_points_credited || 0,
        },
      };

    return {
      error: null,
      response: {
        coupon: global ? global : tentativeRates[0].coupon || undefined,
        total: totalFormatted,
        rates: tentativeRates,
      },
    };
  }

  async getAgentDataById(
    id: string,
  ): Promise<FetchResult<RentalCalendarAgent>> {
    const url = `/api/v2/agents/${id}/dropdown`,
      { response, error } = await GET<AgentOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const {
        name,
        value,
        is_onboarded,
        is_gst_mapped,
        default_gst_value,
        default_tds_value,
      } = response.agent,
      formatted: RentalCalendarAgent = {
        name: name,
        value: value,
        is_onboarded: is_onboarded,
        is_gst_mapped: is_gst_mapped,
        default_gst_value: default_gst_value,
        default_tds_value: default_tds_value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async bookRates(
    opportunity_slug: string,
    rates: TentativeRatesArg[],
    globalCoupon?: Coupon,
    is_gst_mapped?: boolean,
    is_onboarded?: boolean,
    existingReservationSlug?: string,
  ): Promise<FetchResult<void>> {
    const payload: any = {
      reservations: rates.map((each) => {
        const {
            agent_id,
            property_slug,
            checkin_date,
            checkout_date,
            adults,
            children,
            coupon,
            voucher_code,
            reservation_slug,
            breakdown,
            config_id,
            quote_id,
            is_opted,
            display_protect_widget,
          } = each,
          formatted: any = {
            agent_id: agent_id,
            reservation_slug: reservation_slug,
            property_slug: property_slug,
            check_in: checkin_date,
            check_out: checkout_date,
            adults: adults || "1",
            property_config_id: config_id,
            quote_id: quote_id,
            is_opted: is_opted,
            display_protect_widget: display_protect_widget,
            disable_sold_out_checks: reservation_slug ? true : false,
          };

        if (children) {
          formatted.children = children;
        }

        if (coupon) {
          if (coupon.code === "MANUAL") {
            if (coupon.type === "marketing_nights") {
              formatted.discount_type = "marketing_nights";
              formatted.discount_value = 100;
            } else {
              formatted.discount_type = coupon.type;
              formatted.discount_value = coupon.value;
            }
          } else {
            formatted.discount_type = "coupon";
            formatted.discount_value = coupon.code;
          }
        }

        if (voucher_code) {
          formatted.voucher_code = voucher_code;
        }

        // if (globalCoupon && !coupon) {
        //   const { type, value } = globalCoupon;
        //   formatted.discount_type = type;
        //   formatted.discount_value = value;
        // }

        if (breakdown) {
          const {
            deposit,
            preferred_rate,
            agent_cutting_commission,
            commission_gst_applicable,
            commission_tds_applicable,
            commission_percentage,
            gst_commission_percentage,
            //tds_commission_percentage,
            tds,
            acquisition_cost,
            ivpl_budget,
            ivpl_cc_department,
            ihpl_cc_department,
            premium,
            loyalty_points_applied,
          } = breakdown;

          formatted.preferred_rate = preferred_rate;

          formatted.deposit = deposit;

          formatted.agent_cutting_commission = agent_cutting_commission;

          formatted.commission_gst_applicable = commission_gst_applicable;

          formatted.commission_tds_applicable = agent_cutting_commission
            ? commission_tds_applicable
            : true;

          formatted.commission_percentage = commission_percentage;

          formatted.gst_commission_percentage = agent_cutting_commission
            ? gst_commission_percentage
            : commission_gst_applicable
            ? is_onboarded && !is_gst_mapped
              ? 0
              : 18
            : 0;

          //formatted.tds_commission_percentage = commission_tds_applicable ? 2 : tds_commission_percentage ;

          formatted.tds = tds;

          const rawVccb = breakdown.cost_centres;
          const vccb =
            rawVccb &&
            ((rawVccb.ihpl_cost_centres?.length ?? 0) > 0 ||
              (rawVccb.ivpl_cost_centres?.length ?? 0) > 0)
              ? rawVccb
              : undefined;
          if (vccb) {
            // Both voucher and manual cost centres: send sums + cost_centres
            // (matches the pre-today behavior that worked for force_book/
            // multi_update). For an existing voucher reservation, the
            // discount_type/discount_value is already skipped above to avoid
            // double-applying the voucher discount on the backend.
            const enrichItem = (item: any, costCenter: string) => ({
              cost_center: item.cost_center || costCenter,
              department: item.department,
              contribution_type: item.contribution_type || "flat",
              contribution_value:
                item.contribution_value ?? String(item.amount ?? 0),
              amount: item.amount ?? 0,
            });
            const enrichedVccb = {
              ihpl_cost_centres:
                vccb.ihpl_cost_centres?.map((c) => enrichItem(c, "ihpl")) ?? [],
              ivpl_cost_centres:
                vccb.ivpl_cost_centres?.map((c) => enrichItem(c, "ivpl")) ?? [],
            };
            const ihplSum = enrichedVccb.ihpl_cost_centres.reduce(
              (sum, c) => sum + (c.amount ?? 0), 0,
            );
            const ivplSum = enrichedVccb.ivpl_cost_centres.reduce(
              (sum, c) => sum + (c.amount ?? 0), 0,
            );
            formatted.caq = ihplSum;
            formatted.ivpl_budget = ivplSum;
            formatted.cost_centres = enrichedVccb;
          } else {
            formatted.caq = acquisition_cost;
            formatted.ivpl_budget = ivpl_budget;
            formatted.ivpl_cc_department = ivpl_cc_department;
            formatted.ihpl_cc_department = ihpl_cc_department;
          }

          formatted.premium = premium;

          formatted.points_applied = loyalty_points_applied ?? false;
        }

        return formatted;
      }),
    };

    const url = existingReservationSlug
        ? `/api/v2/rental/opportunities/${opportunity_slug}/reservations/update_reservation`
        : `/api/v2/rental/opportunities/${opportunity_slug}/reservations/multi_update`,
      { error } = await PATCH<any>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async blockDates(
    property_slug: string,
    config_id: string,
    start_date: string,
    end_date: string,
    status: string,
    comment: string,
  ): Promise<FetchResult<void>> {
    const payload = {
        property_slug: property_slug,
        property_config_id: config_id,
        check_in: start_date,
        check_out: end_date,
        status: status,
        comment: comment,
      },
      url = "/api/v2/rental/calendar/status",
      { error } = await PATCH<void>(url, payload);
    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async unblockDates(
    property_slug: string,
    config_id: string,
    start_date: string,
    end_date: string,
  ): Promise<FetchResult<void>> {
    const payload = {
        property_slug: property_slug,
        property_config_id: config_id,
        check_in: start_date,
        check_out: end_date,
      },
      url = "/api/v2/rental/calendar/unblock",
      { error } = await DELETE<void>(url, payload);
    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async syncInventory(property_slug: string): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/${property_slug}/sync_inventory`,
      { error } = await POST<void>(url, null);
    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async syncRates(property_slug: string): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/${property_slug}/sync_rates`,
      { error } = await POST<void>(url, null);
    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async uploadCalendarCSV(file: File): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/upload_calendar`,
      formData = new FormData();
    formData.set("file", file);
    const { error } = await POST<void>(url, formData);
    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      response: null,
      error: null,
    };
  }

  async getReservation(
    property_slug: string,
    date: string,
  ): Promise<FetchResult<RentalCalendarDayReservation>> {
    const qp = new URLSearchParams();
    qp.set("property_slug", property_slug);
    qp.set("date", date);

    const url = `/api/v2/rental/calendar/status?${qp}`,
      { error, response } = await GET<ReservationResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const { reservation } = response;
    if (!reservation) {
      return {
        error: new Error("No reservation available"),
        response: null,
      };
    }

    const { opportunity_slug, check_in, check_out, adults, children, name } =
        reservation,
      formatted: RentalCalendarDayReservation = {
        opportunity_slug: opportunity_slug,
        checkin_date: check_in,
        checkout_date: check_out,
        guests: adults.toString(),
        name: name || "",
      };
    if (children) {
      formatted.guests += ` ${children}`;
    }

    return {
      response: formatted,
      error: null,
    };
  }

  async getPropertiesDetails(
    property_slug: string,
  ): Promise<FetchResult<RentalCalendarPropertyDetails>> {
    const url = `/api/v2/rental/properties/${property_slug}/details`,
      { error, response } = await GET<PropertyDetailsResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    const {
        additional_info,
        brochure_url,
        house_rules_url,
        meal_menu_url,
        activity_brochure_url,
      } = response,
      formatted: RentalCalendarPropertyDetails = {
        additional_info: additional_info,
        brochure_url: brochure_url,
        house_rules_url: house_rules_url,
        meal_menu_url: meal_menu_url,
        activities_url: activity_brochure_url,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getMonthlyRevenue(
    property_slug: string,
    start_date: string,
    end_date: string,
  ): Promise<FetchResult<MonthlyRevenueItem[]>> {
    const qp = new URLSearchParams();
    qp.set("property_slug", property_slug);
    qp.set("start_date", start_date);
    qp.set("end_date", end_date);

    const url = `/api/v2/rental/calendar/monthly_revenue?${qp}`,
      { error, response } = await GET<MonthlyRevenueResponse>(url);

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return { error: null, response: response.result };
  }

  async getBlackoutDates(
    start_date: string,
    end_date: string,
  ): Promise<FetchResult<string[]>> {
    const qp = new URLSearchParams();
    qp.set("start_date", start_date);
    qp.set("end_date", end_date);

    const url = `/api/v2/rental/calendar/blackout_dates?${qp}`,
      { error, response } = await GET<BlackoutDatesResponse>(url);

    if (error) {
      return { error, response: null };
    }

    if (!response) {
      return { error: new Error("Unknown error"), response: null };
    }

    return { error: null, response: response.blackout_dates };
  }

  async getCalendarDayDetails(
    property_slug: string,
    property_config_id: string,
    date: string,
  ): Promise<FetchResult<PropertyCalendarBlockDetail | null>> {
    const url = "/api/v2/rental/calendar/details",
      { error, response } = await POST<CalendarDayDetailsResponse>(url, {
        property_slug,
        property_config_id: parseInt(property_config_id, 10),
        date,
      });

    if (error) {
      return { error, response: null };
    }

    if (!response || response.meta?.error) {
      return { error: null, response: null };
    }

    return { error: null, response: response.result ?? null };
  }

  async generateQuote(
    args: TentativeRatesArg[],
    opportunitySlug?: string,
  ): Promise<FetchResult<GenerateQuote>> {
    if (!args.length) {
      return {
        error: null,
        response: null,
      };
    }

    const payload: any = {
      reservations: args.map((each) => {
        const {
            agent_id,
            property_slug,
            checkin_date,
            checkout_date,
            adults,
            children,
            coupon,
            voucher_code,
            breakdown,
            config_id,
            is_opted,
            display_protect_widget,
            quote_id,
          } = each,
          formatted: any = {
            agent_id: agent_id,
            property_slug: property_slug,
            check_in: checkin_date,
            check_out: checkout_date,
            adults: adults || "1",
            property_config_id: config_id,
            is_opted: is_opted,
            display_protect_widget: display_protect_widget,
            quote_id: quote_id,
          };

        if (children) {
          formatted.children = children;
        }

        if (coupon) {
          if (coupon.code === "MANUAL") {
            formatted.discount_type = coupon.type;
            formatted.discount_value = coupon.value;
          } else {
            formatted.discount_type = "coupon";
            formatted.discount_value = coupon.code;
          }
        }

        if (voucher_code) {
          formatted.voucher_code = voucher_code;
        }

        if (breakdown) {
          const {
            deposit,
            preferred_rate,
            agent_cutting_commission,
            commission_gst_applicable,
            commission_tds_applicable,
            commission_percentage,
            gst_commission_percentage,
            tds_commission_percentage,
            tds,
            acquisition_cost,
            ivpl_budget,
            ivpl_cc_department,
            ihpl_cc_department,
            premium,
            coupon_code,
            loyalty_points_applied,
            cost_centres,
          } = breakdown;

          formatted.preferred_rate = preferred_rate;

          formatted.deposit = deposit;

          formatted.agent_cutting_commission = agent_cutting_commission;

          formatted.commission_gst_applicable = commission_gst_applicable;

          formatted.commission_tds_applicable = commission_tds_applicable;

          formatted.commission_percentage = commission_percentage;

          formatted.gst_commission_percentage = gst_commission_percentage;

          formatted.tds_commission_percentage = tds_commission_percentage;

          formatted.tds = tds;

          const rawVccb = cost_centres;
          const vccb =
            rawVccb &&
            ((rawVccb.ihpl_cost_centres?.length ?? 0) > 0 ||
              (rawVccb.ivpl_cost_centres?.length ?? 0) > 0)
              ? rawVccb
              : undefined;

          if (vccb) {
            const ihplSum = vccb.ihpl_cost_centres?.reduce(
              (sum: number, c: any) => sum + (c.amount ?? 0), 0,
            ) ?? 0;
            const ivplSum = vccb.ivpl_cost_centres?.reduce(
              (sum: number, c: any) => sum + (c.amount ?? 0), 0,
            ) ?? 0;
            formatted.caq = ihplSum;
            formatted.ivpl_budget = ivplSum;
            formatted.cost_centres = vccb;
          } else {
            formatted.caq = acquisition_cost;
            formatted.ivpl_budget = ivpl_budget;
            formatted.ivpl_cc_department = ivpl_cc_department;
            formatted.ihpl_cc_department = ihpl_cc_department;
          }

          formatted.premium = premium;

          formatted.coupon_code = coupon_code;

          formatted.loyalty_points_applied = loyalty_points_applied ?? false;

          formatted.points_applied = loyalty_points_applied ?? false;
        }
        return formatted;
      }),
    };

    if (opportunitySlug) {
      payload.opportunity_slug = opportunitySlug || null;
    }

    const url = "/api/v2/rental/property_quotations",
      { error, response } = await POST<GenerateQuoteResponse>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown error"),
        response: null,
      };
    }

    return {
      error: error,
      response: response,
    };
  }
}

export default RentalCalendarService;

type RentalCalendarResponse = {
  properties: RentalCalendarProperty[];
  paginate: Pagination;
};

type TentativeRatesResponse = {
  rates: any;
  total: any;
};

type ReservationResponse = {
  reservation: any;
};

type PropertyDetailsResponse = {
  additional_info: string;
  brochure_url: string;
  house_rules_url: string;
  meal_menu_url: string;
  activity_brochure_url: string;
};

type AgentOptionById = {
  agent: any;
};

type GenerateQuoteResponse = {
  message: any;
};

type MonthlyRevenueResponse = {
  result: MonthlyRevenueItem[];
  meta: { error: boolean };
};

type CalendarDayDetailsResponse = {
  result: PropertyCalendarBlockDetail | null;
  meta: { error: boolean };
};

type BlackoutDatesResponse = {
  blackout_dates: string[];
  meta: { error: boolean };
};
